import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import {
  ArrowRight,
  Inbox,
  CircleDollarSign,
  Truck,
  Building2,
  Trophy,
  AlertTriangle,
  Plane,
} from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import {
  api,
  type CoverageGapsResponse,
  type DashboardOverviewResponse,
} from '@/lib/api';
import { AcceptedAwaitingActionPanel } from '@/components/accepted-awaiting-action';
import { BuyersWaitingPanel } from '@/components/buyers-waiting';

function fmtMoney(cents: number, currency = 'USD'): string {
  if (currency === 'JPY') return `¥${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function DashboardPage() {
  const { getToken } = useAuth();

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['dashboard-overview'],
    queryFn: () =>
      api<DashboardOverviewResponse>('/api/dashboard/overview', {
        tokenGetter: () => getToken(),
      }),
    staleTime: 30_000,
  });

  const { data: gaps, isLoading: gapsLoading } = useQuery({
    queryKey: ['coverage-gaps'],
    queryFn: () =>
      api<CoverageGapsResponse>('/api/vendors/coverage-gaps', {
        tokenGetter: () => getToken(),
      }),
    staleTime: 60_000,
  });

  const k = overview?.kpis;
  const gapsTotal =
    (gaps?.platformsWithThinCoverage.length ?? 0) + (gaps?.untaggedPlatforms.length ?? 0);

  return (
    <AppShell
      title="Dashboard"
      subtitle="Pipeline at a glance — last 30 days"
    >
      <div className="h-full overflow-auto px-6 py-6">
        {/* An accepted quote is the only thing on this page that is a TASK
            rather than a statistic, so it sits above the tiles and renders
            nothing at all when there is none. */}
        <AcceptedAwaitingActionPanel variant="band" />
        <BuyersWaitingPanel variant="band" />
        {(k?.shippedAwaitingPayment?.count ?? 0) > 0 && (
          <Link
            href="/orders?filter=awaiting_payment"
            className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 hover:bg-amber-100"
          >
            <CircleDollarSign className="h-4 w-4 shrink-0" aria-hidden />
            <span className="font-medium">
              {k!.shippedAwaitingPayment!.count} shipped order{k!.shippedAwaitingPayment!.count === 1 ? '' : 's'} awaiting payment
            </span>
            <span className="font-mono text-xs">
              {k!.shippedAwaitingPayment!.byCurrency
                .map((c) => (c.currency === 'USD' || c.currency === 'JPY'
                  ? fmtMoney(c.amountDueCents, c.currency)
                  : `${c.currency} ${(c.amountDueCents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`))
                .join(' · ')}
            </span>
            <span className="ml-auto inline-flex items-center gap-1 text-xs font-medium">View <ArrowRight className="h-3 w-3" /></span>
          </Link>
        )}

        {/* KPI tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiTile
            icon={<Inbox className="w-5 h-5" />}
            label="Open RFQs"
            value={overviewLoading ? '…' : String(k?.openRfqs ?? 0)}
            tone="primary"
          />
          <KpiTile
            icon={<CircleDollarSign className="w-5 h-5" />}
            label="Paid · 30d"
            value={overviewLoading ? '…' : fmtMoney(k?.paid30dTotalCents ?? 0)}
            sub={overviewLoading ? '' : `${k?.paid30dCount ?? 0} invoice${k?.paid30dCount === 1 ? '' : 's'}`}
            tone="success"
          />
          <KpiTile
            icon={<Truck className="w-5 h-5" />}
            label="In motion"
            value={overviewLoading ? '…' : String(k?.shipmentsActive ?? 0)}
            sub="Active shipments"
            tone="info"
          />
          <KpiTile
            icon={<Trophy className="w-5 h-5" />}
            label="Win rate · 30d"
            value={
              overviewLoading
                ? '…'
                : k?.winRate30d == null
                  ? '—'
                  : `${k.winRate30d.toFixed(0)}%`
            }
            sub={overviewLoading ? '' : `${k?.accepted30d ?? 0} accepted`}
            tone="warning"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Recent RFQs (2 columns) */}
          <section className="card lg:col-span-2 overflow-hidden">
            <header className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-sm">Recent RFQs</h2>
              <Link
                href="/rfqs"
                className="text-xs font-medium text-primary hover:text-primary-strong inline-flex items-center gap-1"
              >
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            </header>
            <ul className="divide-y divide-border">
              {overviewLoading && (
                <li className="px-5 py-6 text-sm text-foreground-muted">Loading…</li>
              )}
              {!overviewLoading && (overview?.recentRfqs.length ?? 0) === 0 && (
                <li className="px-5 py-12 text-sm text-foreground-muted text-center">
                  No RFQs yet. Parse one from email or wait for a web-form submission.
                </li>
              )}
              {overview?.recentRfqs.map((r) => (
                <li key={r.publicId}>
                  <Link
                    href={`/rfqs/${r.publicId}`}
                    className="block px-5 py-3 hover:bg-surface-muted transition-colors"
                  >
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="flex items-baseline gap-2">
                        <Plane className="w-3.5 h-3.5 text-foreground-muted -mb-0.5" />
                        <span className="font-mono text-sm font-medium">{r.ref}</span>
                        {r.isAog && <span className="pill pill-danger">AOG</span>}
                        {r.isUrgent && !r.isAog && <span className="pill bg-amber-500 text-white">URGENT</span>}
                      </div>
                      <div className="text-xs text-foreground-muted">
                        {r.sourceChannel ? (
                          <span className="pill pill-default">{r.sourceChannel}</span>
                        ) : null}
                        <span className="ml-2">{new Date(r.receivedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          {/* Side rail — Vendors + Coverage gaps */}
          <div className="space-y-4">
            <section className="card overflow-hidden">
              <header className="px-5 py-3 border-b border-border">
                <h2 className="font-semibold text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-foreground-muted" />
                  Vendor roster
                </h2>
              </header>
              <div className="px-5 py-4">
                <div className="text-3xl font-bold tabular-nums tracking-tight">
                  {overviewLoading ? '…' : (k?.vendorsTotal ?? 0)}
                </div>
                <div className="text-xs text-foreground-muted mt-1">
                  Total vendors in roster
                </div>
                <Link
                  href="/vendors"
                  className="mt-3 btn-outline text-xs w-full justify-center"
                >
                  Manage vendors <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </section>

            <section className="card overflow-hidden">
              <header className="px-5 py-3 border-b border-border flex items-center justify-between">
                <h2 className="font-semibold text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  Coverage gaps
                </h2>
                {gapsTotal > 0 && (
                  <span className="pill pill-warning">{gapsTotal}</span>
                )}
              </header>
              <div className="px-5 py-3 space-y-2">
                {gapsLoading && <div className="text-xs text-foreground-muted">Loading…</div>}
                {!gapsLoading && gapsTotal === 0 && (
                  <div className="text-xs text-foreground-muted italic">
                    No gaps detected — every active platform has ≥2 vendors.
                  </div>
                )}
                {gaps?.platformsWithThinCoverage.slice(0, 4).map((p) => (
                  <div key={p.platform} className="flex items-baseline justify-between text-xs">
                    <span className="font-mono font-medium">{p.platform}</span>
                    <span className="text-foreground-muted">
                      {p.vendorCount} vendor{p.vendorCount === 1 ? '' : 's'} · {p.rfqCount} RFQ{p.rfqCount === 1 ? '' : 's'}
                    </span>
                  </div>
                ))}
                {gapsTotal > 0 && (
                  <Link
                    href="/coverage-gaps"
                    className="text-xs font-medium text-primary hover:text-primary-strong inline-flex items-center gap-1 mt-2"
                  >
                    See all gaps <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// KpiTile — used four times on the dashboard
// ---------------------------------------------------------------------------

const TONE: Record<'primary' | 'success' | 'info' | 'warning', { iconBg: string; iconText: string }> = {
  primary: { iconBg: 'bg-primary-muted', iconText: 'text-primary-strong' },
  success: { iconBg: 'bg-success-muted', iconText: 'text-success' },
  info:    { iconBg: 'bg-info-muted',    iconText: 'text-primary-strong' },
  warning: { iconBg: 'bg-warning-muted', iconText: 'text-warning' },
};

function KpiTile({
  icon,
  label,
  value,
  sub,
  tone = 'primary',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone?: keyof typeof TONE;
}) {
  const t = TONE[tone];
  return (
    <div className="stat-tile">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="stat-tile-label">{label}</div>
          <div className="stat-tile-value">{value}</div>
          {sub && <div className="stat-tile-delta">{sub}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md ${t.iconBg} ${t.iconText} flex items-center justify-center flex-shrink-0`}>
          {icon}
        </div>
      </div>
    </div>
  );
}
